import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import {
    Button,
    Form,
    Table,
    Row,
    Col,
    ButtonGroup,
    ListGroup,
    Dropdown,
    Container,
    Tab,
    Tabs,
    Badge,
    Accordion,
    InputGroup,
    Overlay,
    Popover,
    Alert
} from 'react-bootstrap';
import '../styles/main.css';
import { config } from '../config.js';
import { GetConnectionStatus } from "../utils/GetConnectionStatus";
import GroupNameModal from "./GroupNameModal.jsx";
import { Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { isAuthorized } from "../utils/Permissions.js";
import { apiRequest, ecsRequest } from "../utils/apiClient.js";
import { BROKERAGE_SCHWAB } from "../utils/constants.js";
import { TradingZoneModal } from "./TradingZones/TradingZonesModal.jsx";
import { InfoHover } from "./InfoHover/InfoHover.jsx";
import { Slider } from "./ui/Slider.jsx";
import { TradingZonesTable } from "./TradingZones/TradingZonesTable.jsx";
import { LoadingScreen } from "./LoadingScreen.jsx";
import { TRADE_TEMPLATES } from "./tradeTemplates.js";
import { TemplateConfirmModal } from "./TemplateConfirmModal.jsx";

export const UserControls = () => {
    const navigate = useNavigate();
    const { getAccessTokenSilently, user } = useAuth0();
    const target = useRef(null);
    const userId = user.sub.split('|')[1];
    const username = user && user.name ? user.name : '';

    const [tradeConfig, setTradeConfig] = useState(undefined);
    const [fullTradeConfig, setFullTradeConfig] = useState(undefined);
    const [algoOptions, setAlgoOptions] = useState([]);
    const [savedAlgos, setSavedAlgos] = useState([]);
    const [selectedAlgo, setSelectedAlgo] = useState({});
    const [selectedTicker, setSelectedTicker] = useState({});

    const emptySettingsGroupForm = {
        riskSize: 500,
        maxRiskSize: 1000,
        riskMode: 'fixed',
        riskPercent: 1,
        numContracts: 1,
        profitTarget: 2,
        contractLimit: 20,
        dailyLossLimit: 2000,
        dailyProfitLimit: 3000,
        isTradingEnabled: false,
        stackTrades: false,
        useTrailingStop: false,
        trailingStopSettings: {
            stopSize: 1,
            trigger: 0.9,
            frequency: 1,
        },
        accountsToTrade: [],
        tradeLog: [],
        fullTradeLog: [],
        isConnected: false,
        isTradingActive: false,
        accountData: [],
        activeBuid: '',
        accountStatus: [],
        activeAccountNickname: '',
        groupName: 'default',
        savedAlgos: [],
        tradingZones: [],
        singleDirectionTrading: false,
        maxTradesPerDay: '',
    };

    const defaultMasterTradeSettings = {
        isMasterTradingEnabled: true,
        isMasterAutomaticallyEnabled: true,
    };

    const [allAccounts, setAllAccounts] = useState([]);
    const [settingsGroups, setSettingsGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(
        emptySettingsGroupForm.groupName
    );
    const [showModal, setShowModal] = useState(false);
    const [isNewGroup, setIsNewGroup] = useState(false);
    const [showRiskPreview, setShowRiskPreview] = useState(false);
    const riskPreviewTargetRef = useRef(null);
    const [showRiskDisclosure, setShowRiskDisclosure] = useState(false);
    const [accountStatusList, setAccountStatusList] = useState([]);
    const [connections, setConnections] = useState({});
    const [activeBuid, setActiveBuid] = useState('');
    const [tradeLogBuid, setTradeLogBuid] = useState([]);
    const [fullTradeLogBuid, setFullTradeLogBuid] = useState([]);
    const [tradeLog, setTradeLog] = useState([]);
    const [fullTradeLog, setFullTradeLog] = useState([]);
    const [showTradingZoneModal, setShowTradingZoneModal] = useState(false);
    const [isNewTradingZone, setIsNewTradingZone] = useState(true);
    const [editTradingZone, setEditTradingZone] = useState();
    const [connectionNeedsSave, setConnectionNeedsSave] = useState({});
    const [masterTradeSettings, setMasterTradeSettings] = useState(defaultMasterTradeSettings);
    const [isLoading, setIsLoading] = useState(true);
    const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
    const [pendingTemplate, setPendingTemplate] = useState(null);

    const currentSettingsGroup = settingsGroups.find(
        (group) => group.groupName === selectedGroupId
    ) ?? {};

    // True when the current group has a Last Son "1m-pullback" algo selected. Checks both the
    // savedAlgos state (objects {name, ticker, timeframe} — what the Saved Algorithms table renders,
    // populated on load by SetAlgoList) and currentSettingsGroup.savedAlgos (ID strings of the form
    // "Name_Ticker_Timeframe"), since the two can be briefly out of sync after a page reload.
    const isLastSonPullbackId = (value) => {
        if (value && typeof value === 'object') {
            return value.name === 'LastSon' && value.timeframe === '1m-pullback';
        }
        const parts = String(value).split('_');
        return parts[0] === 'LastSon' && parts[parts.length - 1] === '1m-pullback';
    };
    const hasLastSonPullbackSelected =
        (Array.isArray(savedAlgos) && savedAlgos.some(isLastSonPullbackId)) ||
        (currentSettingsGroup.savedAlgos ?? []).some(isLastSonPullbackId);

    const handleSaveGroupName = (groupName) => {
        if (isNewGroup) {
            setSettingsGroups([
                ...settingsGroups,
                {
                    ...emptySettingsGroupForm,
                    groupName: groupName,
                    accountsToTrade: tradeConfig.accountData.map((account) => {
                        return {
                            name: account.name,
                            enableTrading: false,
                            amount: account.amount,
                            isAuthorized: account.isAuthorized,
                        }
                    })
                },
            ]);
        } else {
            setSettingsGroups(
                settingsGroups.map((group) => {
                    if (group.groupName === currentSettingsGroup.groupName) {
                        return {
                            ...currentSettingsGroup,
                            groupName: groupName,
                        };
                    }
                    return group;
                })
            );
        }

        setSelectedGroupId(groupName);
        setSavedAlgos([]);
        setShowModal(false);
        setSelectedAlgo({});
        setSelectedTicker({});
    };

    const handleChange = (e) => {
        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return {
                        ...currentSettingsGroup,
                        [e.target.name]: e.target.value,
                    };
                }
                return group;
            })
        );
    };

    const handleTrailingStopChange = (e) => {
        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    const key = e.target.name.split('.')[1];
                    const trailingStopSettings = {
                        ...currentSettingsGroup.trailingStopSettings,
                        [key]: e.target.value,
                    };
                    return {
                        ...currentSettingsGroup,
                        trailingStopSettings,
                    };
                }
                return group;
            })
        );
    };

    const handleCheckboxChange = (e) => {
        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return {
                        ...currentSettingsGroup,
                        [e.target.name]: e.target.checked,
                    };
                }
                return group;
            })
        );
    };

    const handleMasterTradingCheckboxChange = async (e) => {
        const tradeSettings = {
            ...masterTradeSettings,
            [e.target.id]: e.target.checked,
        };
        setMasterTradeSettings(tradeSettings)
        const body = { masterTradeSettings: tradeSettings };
        await updateTradeConfig(body);
    };

    const handleAccountToTradeChange = (e) => {
        const index = currentSettingsGroup.accountsToTrade.findIndex(
            (obj) => obj.name === e.target.name
        );
        const accountsToTrade = [...currentSettingsGroup.accountsToTrade];
        accountsToTrade[index].enableTrading = e.target.checked;

        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return {
                        ...currentSettingsGroup,
                        accountsToTrade,
                    };
                }
                return group;
            })
        );
    };

    useEffect(() => {
        const GetTradeConfig = async () => {
            const token = await getAccessTokenSilently();
            const headers = {
                'Authorization': `Bearer ${token}`,
            };
            const params = {
                uid: userId,
                buid: activeBuid
            };
            try {
                const tradeConfig = await apiRequest('get', '/algo/demo/trade/config', '/api/v2/trade/config', {
                    headers, params,
                });


                //If maxRiskSize is null anywhere, set it to the default
                if (
                    tradeConfig.data &&
                    tradeConfig.data[activeBuid] &&
                    tradeConfig.data[activeBuid].accountGroups &&
                    tradeConfig.data[activeBuid].accountGroups.length
                ) {
                    for (const group of tradeConfig.data[activeBuid].accountGroups) {
                        if (!group.maxRiskSize) {
                            group.maxRiskSize = 1000;
                        }
                        if (!group.dailyLossLimit) {
                            group.dailyLossLimit = 2000;
                        }
                        if (!group.dailyProfitLimit) {
                            group.dailyProfitLimit = 3000;
                        }
                        if (group.maxTradesPerDay === undefined || group.maxTradesPerDay === null) {
                            group.maxTradesPerDay = '';
                        }
                        if (group.numContracts === undefined || group.numContracts === null) {
                            group.numContracts = 1;
                        }
                    }
                }
                return tradeConfig.data;
            } catch (error) {
                console.log(error.message);
                return error.message;
            }
        };

        const getTradeConfig = async () => {
            if (activeBuid) {
                const configResp = await GetTradeConfig();
                if (configResp && typeof configResp === 'object') {
                    setFullTradeConfig(configResp);
                    const tradeConfig = configResp[activeBuid];
                    setTradeConfig(tradeConfig);
                    setMasterTradeSettings(configResp.masterTradeSettings ? configResp.masterTradeSettings : defaultMasterTradeSettings);
                    if (!tradeConfig) {
                        setIsLoading(false);
                    }
                } else {
                    setIsLoading(false);
                }
            }
        };

        getTradeConfig();
    }, [activeBuid]);

    //useEffect for setting saved algo list
    useEffect(() => {
        if (activeBuid && tradeConfig) {
            SetAlgoList(selectedGroupId);
        }
    }, [tradeConfig, settingsGroups]);

    useEffect(() => {

        if (tradeConfig) {
            setAllAccounts(tradeConfig.accountData);
            if (tradeConfig.accountGroups?.length) {
                //Search all accounts
                for (const account of tradeConfig.accountData) {
                    for (const group of tradeConfig.accountGroups) {
                        const foundAccount = group.accountsToTrade.find((a) => a.name === account.name);
                        if (foundAccount) {
                            for (const atTrade of group.accountsToTrade) {
                                if (atTrade.name === foundAccount.name) {
                                    atTrade.amount = account.amount;
                                    atTrade.isAuthorized = account.isAuthorized;
                                }
                            }
                        } else {
                            group.accountsToTrade.push({
                                name: account.name,
                                enableTrading: false,
                                amount: account.amount,
                                isAuthorized: account.isAuthorized,
                            });
                        }
                    }
                }
            }
            setSettingsGroups(tradeConfig.accountGroups?.length ? tradeConfig.accountGroups : [emptySettingsGroupForm]);
            setSelectedGroupId(tradeConfig.accountGroups?.length ? tradeConfig.accountGroups[0].groupName : emptySettingsGroupForm.groupName);
            setIsLoading(false);
        }
    }, [tradeConfig]);

    useEffect(() => {
        const orchestrator = async () => {
            try {
                const connections = await getConnectionStatus();
                let tempAccountStatusList = [];
                for (const buid in connections) {
                    tempAccountStatusList.push({
                        name: connections[buid].name,
                        isConnected: connections[buid].isConnected,
                        isActiveConnection: connections[buid].isActiveConnection,
                        id: buid,
                    });
                }

                setAccountStatusList(tempAccountStatusList);
                setConnections(connections);

                if (!tempAccountStatusList.length) {
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Failed to fetch connection status:', error);
                setIsLoading(false);
            }
        }
        const getConnectionStatus = async () => {
            const token = await getAccessTokenSilently();
            const status = await GetConnectionStatus(token, userId);
            return status.data;
        };
        orchestrator();
    }, [])

    useEffect(() => {
        if (accountStatusList.length) {
            const tempSettingsGroup = {
                ...currentSettingsGroup,
            };

            let tempActiveBuid = '';

            if (
                window.location.pathname.split("/").length > 2 &&
                window.location.pathname.split("/")[2]
            ) {
                tempActiveBuid = window.location.pathname.split("/")[2];
                tempSettingsGroup.activeBuid = activeBuid;
            } else {
                tempActiveBuid = accountStatusList[0].id;

                tempSettingsGroup.activeBuid = activeBuid;
            }
            tempSettingsGroup.isConnected = !!connections[activeBuid]?.isConnected;
            tempSettingsGroup.activeAccountNickname = connections[activeBuid]?.name ?? "";

            setActiveBuid(tempActiveBuid);
            setSettingsGroups(
                settingsGroups.map((group) => {
                    if (group.groupName === currentSettingsGroup.groupName) {
                        return tempSettingsGroup;
                    }
                    return group;
                })
            );
        }
    }, [accountStatusList, connections, activeBuid])

    const GetTradeLog = async (type) => {
        const token = await getAccessTokenSilently();
        const headers = {
            'Authorization': `Bearer ${token}`,
        };
        const params = {
            uid: userId,
            buid: activeBuid
        };
        try {
            const ecsPath = type === 'full' ? '/algo/demo/optTradeLog' : '/algo/demo/tradeLog';
            const tradeLog = await ecsRequest('get', ecsPath, { headers, params });
            return tradeLog ? tradeLog.data : [];
        } catch (error) {
            //console.log(error.message);
            return error.message;
        }
    };

    const getTradeLog = async (type) => {
        const resp = await GetTradeLog(type);
        const tradeLog = resp[activeBuid];
        if (tradeLog && Object.keys(tradeLog).length) {
            if (type === 'full') {
                setFullTradeLogBuid(tradeLog);
            } else {
                setTradeLogBuid(tradeLog);
            }
        }
    };

    useEffect(() => {
        //getTradeLog('full');
        getTradeLog('personal');
    }, [activeBuid]);

    useEffect(() => {
        if (tradeLogBuid && tradeLogBuid[selectedGroupId]) {

            setTradeLog(tradeLogBuid[selectedGroupId]);
            setFullTradeLog(fullTradeLogBuid[selectedGroupId]);
        }
    }, [tradeLogBuid, selectedGroupId]);

    useEffect(() => {
        // fetch from api and set state
        const getAlgoConfig = async () => {
            const token = await getAccessTokenSilently();
            const headers = {
                'Authorization': `Bearer ${token}`,
            };
            const params = {
                uid: userId,
                buid: activeBuid
            };
            try {
                const algoConfigResp = await apiRequest('get', '/algo/config', '/api/v2/algo/config', {
                    headers, params,
                });

                setAlgoOptions(algoConfigResp.data);
            } catch (error) {
                console.log(error.message);
            }
        }
        if (!algoOptions || algoOptions.length === 0) {
            getAlgoConfig();
        }
    }, []);

    useEffect(() => {
        if (savedAlgos && Array.isArray(savedAlgos)) {
            currentSettingsGroup.savedAlgos = savedAlgos.map((algo) => {
                return algo.id;
            });
        }
    }, [savedAlgos]);

    const handleAlgoSelection = (algoName) => {
        if (algoName) {
            const algo = algoOptions.find((algo) => algo.algoName === algoName);
            if (algo) {
                setSelectedAlgo(algo);
                setSelectedTicker({});
            }
        }
    };

    const handleTickerSelection = (tickerName) => {
        if (tickerName && selectedAlgo && selectedAlgo.tickers) {
            setSelectedTicker({
                tickerName,
                timeframes: selectedAlgo.tickers[tickerName].timeframes.filter((timeframe) => {
                    if (savedAlgos.find((algo) => algo.id === `${selectedAlgo.algoName}_${tickerName}_${timeframe}`)) {
                        return false;
                    }
                    return true;
                }),
            });
        }
    };

    const sortAlgos = (algosList, sortByList) => {
        for (const prop of sortByList) {
            algosList.sort((a, b) => {
                if (a[prop] > b[prop]) {
                    return 1;
                }
                if (a[prop] < b[prop]) {
                    return -1;
                }
                return 0;
            });
        }
        return algosList;
    };

    const handleTimeFrameSelection = (selectedTimeFrame) => {
        if (selectedTimeFrame) {
            const tempAlgoOptions = [...algoOptions];

            setAlgoOptions(
                tempAlgoOptions.map((algoOption) => {
                    if (algoOption.algoName === selectedAlgo.algoName) {
                        // algoOption.tickers[selectedTicker.tickerName].timeframes =
                        //     algoOption.tickers[selectedTicker.tickerName].timeframes.filter(
                        //         (timeframe) => !!(timeframe !== selectedTimeFrame && !savedAlgos.find((algo) => algo.id === `${algoOption.algoName}_${selectedTicker.tickerName}_${timeframe}`))
                        //     );

                        setSelectedTicker({
                            tickerName: selectedTicker.tickerName,
                            timeframes:
                                algoOption.tickers[selectedTicker.tickerName].timeframes,
                        });
                    }

                    return algoOption;
                })
            );

            const algoId = `${selectedAlgo.algoName}_${selectedTicker.tickerName}_${selectedTimeFrame}`;
            const algo = savedAlgos.find((algo) => algo.id === algoId);

            if (!algo) {
                const savedList = [
                    ...savedAlgos,
                    {
                        id: algoId,
                        name: selectedAlgo.algoName,
                        ticker: selectedTicker.tickerName,
                        timeframe: selectedTimeFrame,
                        new: true,
                    },
                ];

                sortAlgos(savedList, ["timeframe", "ticker", "name"]);

                setSavedAlgos(savedList);
            }
        }
    };

    const handleRemoveSelection = (removedAlgo) => {
        setSavedAlgos(
            savedAlgos.filter((savedAlgo) => savedAlgo.id !== removedAlgo.id)
        );

        const tempAlgoOptions = [...algoOptions];
        setAlgoOptions(
            tempAlgoOptions.map((algoOption) => {
                if (algoOption.algoName === removedAlgo.name) {
                    if (
                        !algoOption.tickers[removedAlgo.ticker].timeframes.includes(
                            removedAlgo.timeframe
                        )
                    ) {
                        algoOption.tickers[removedAlgo.ticker].timeframes.push(
                            removedAlgo.timeframe
                        );
                        algoOption.tickers[removedAlgo.ticker].timeframes.sort((a, b) => {
                            if (a > b) {
                                return 1;
                            }
                            if (a < b) {
                                return -1;
                            }
                            return 0;
                        });
                    }
                }

                return algoOption;
            })
        );
    };

    const ClearForm = (e) => {
        const clearedSettingsForm = {
            ...currentSettingsGroup,
            riskSize: 500,
            maxRiskSize: 1000,
            dailyLossLimit: 2000,
            dailyProfitLimit: 3000,
            isTradingEnabled: false,
            stackTrades: false,
            useTrailingStop: false,
            trailingStopSettings: {
                stopSize: 1,
                trigger: 1,
                frequency: 1,
            },
            savedAlgos: [],
            singleDirectionTrading: false,
            maxTradesPerDay: '',
            numContracts: 1,
        };

        if (clearedSettingsForm.accountsToTrade) {
            let newAccounts = [...clearedSettingsForm.accountsToTrade];
            for (const account of newAccounts) {
                account.enableTrading = false;
            }
            clearedSettingsForm.accountsToTrade = newAccounts;
        }

        setSettingsGroups(
            settingsGroups.map((group) => {
                if (group.groupName === currentSettingsGroup.groupName) {
                    return clearedSettingsForm;
                }
                return group;
            })
        );
    };

    const handleTemplateClick = (templateName) => {
        setPendingTemplate(templateName);
        setShowTemplateConfirm(true);
    };

    const applySettingsToGroup = (group, settings) => {
        return {
            ...group,
            riskSize: settings.riskSize,
            maxRiskSize: settings.maxRiskSize,
            riskMode: settings.riskMode ?? group.riskMode,
            riskPercent: settings.riskPercent ?? group.riskPercent,
            numContracts: settings.numContracts ?? group.numContracts,
            profitTarget: settings.profitTarget,
            contractLimit: settings.contractLimit,
            dailyLossLimit: settings.dailyLossLimit,
            dailyProfitLimit: settings.dailyProfitLimit,
            stackTrades: settings.stackTrades,
            singleDirectionTrading: settings.singleDirectionTrading,
            maxTradesPerDay: settings.maxTradesPerDay ?? '',
            savedAlgos: settings.savedAlgos.length > 0
                ? settings.savedAlgos
                : group.savedAlgos,
        };
    };

    // Build a human-readable preview of what a template will create: one row per
    // group with its algorithms and its size (contracts / $ risk / % risk).
    const buildTemplatePreview = (templateName) => {
        const template = TRADE_TEMPLATES.find(t => t.name === templateName);
        if (!template) return [];

        const prettyAlgos = (savedAlgos = []) => {
            // Group tickers by algo name so "DoubleDip_SPX, DoubleDip_XSP" reads
            // as "Double Dip (SPX, XSP)".
            const byAlgo = {};
            for (const a of savedAlgos) {
                const [name, ticker] = a.split('_');
                if (!byAlgo[name]) byAlgo[name] = [];
                if (ticker && !byAlgo[name].includes(ticker)) byAlgo[name].push(ticker);
            }
            const spaced = (n) => n.replace(/([a-z])([A-Z])/g, '$1 $2');
            return Object.entries(byAlgo)
                .map(([name, tickers]) => tickers.length ? `${spaced(name)} (${tickers.join(', ')})` : spaced(name))
                .join(', ');
        };

        const sizeLabel = (settings = {}) => {
            if (settings.riskMode === 'contracts' && Number(settings.numContracts) >= 1) {
                const n = Number(settings.numContracts);
                return `${n} contract${n === 1 ? '' : 's'}`;
            }
            if (settings.riskMode === 'percent' && settings.riskPercent) {
                return `${settings.riskPercent}% risk`;
            }
            if (settings.riskSize) {
                return `$${Number(settings.riskSize).toLocaleString()} risk`;
            }
            return '—';
        };

        const resolveSettings = (def) => {
            if (def.isReference) {
                const ref = TRADE_TEMPLATES.find(t => t.name === def.templateName);
                return { groupName: def.groupName, settings: ref?.settings || {} };
            }
            if (def.settings) return { groupName: def.name, settings: def.settings };
            return { groupName: def.groupName || def.name, settings: def };
        };

        const groups = template.type === 'multi-group'
            ? template.groups.map(resolveSettings)
            : [{ groupName: pendingTemplate, settings: template.settings }];

        return groups.map(({ groupName, settings }) => ({
            groupName,
            algos: prettyAlgos(settings?.savedAlgos),
            size: sizeLabel(settings),
        }));
    };

    const handleApplyTemplate = () => {
        setShowTemplateConfirm(false);
        const template = TRADE_TEMPLATES.find(t => t.name === pendingTemplate);
        if (!template) return;
        setPendingTemplate(null);

        if (template.type === 'multi-group') {
            const accountsToTrade = tradeConfig?.accountData?.map((account) => ({
                name: account.name,
                enableTrading: false,
                amount: account.amount,
                isAuthorized: account.isAuthorized,
            })) || [];

            const resolvedGroups = template.groups.map((groupDef) => {
                if (groupDef.isReference) {
                    const ref = TRADE_TEMPLATES.find(t => t.name === groupDef.templateName);
                    return { groupName: groupDef.groupName, ...ref?.settings };
                }
                // Flatten: groups define settings nested under .settings
                return { groupName: groupDef.name, ...groupDef.settings };
            });

            let updatedGroups = [...settingsGroups];
            for (const groupDef of resolvedGroups) {
                const existingIndex = updatedGroups.findIndex(g => g.groupName === groupDef.groupName);
                if (existingIndex >= 0) {
                    updatedGroups[existingIndex] = applySettingsToGroup(updatedGroups[existingIndex], groupDef);
                } else {
                    updatedGroups.push(applySettingsToGroup({
                        ...emptySettingsGroupForm,
                        groupName: groupDef.groupName,
                        accountsToTrade,
                    }, groupDef));
                }
            }
            setSettingsGroups(updatedGroups);

            const firstGroup = resolvedGroups[0];
            setSelectedGroupId(firstGroup.groupName);
            if (firstGroup.savedAlgos?.length > 0) {
                const parsed = firstGroup.savedAlgos.map((algo) => {
                    const [name, ticker, timeframe] = algo.split('_');
                    return { id: algo, name, ticker, timeframe };
                });
                setSavedAlgos(parsed);
            }
        } else {
            // Resolve reference templates to get the actual settings
            let settings = template.settings;
            if (settings.isReference) {
                const ref = TRADE_TEMPLATES.find(t => t.name === settings.templateName);
                settings = { groupName: settings.groupName, ...ref?.settings };
            }

            setSettingsGroups(
                settingsGroups.map((group) => {
                    if (group.groupName === currentSettingsGroup.groupName) {
                        return applySettingsToGroup(currentSettingsGroup, settings);
                    }
                    return group;
                })
            );

            if (settings.savedAlgos?.length > 0) {
                const parsed = settings.savedAlgos.map((algo) => {
                    const [name, ticker, timeframe] = algo.split('_');
                    return { id: algo, name, ticker, timeframe };
                });
                setSavedAlgos(parsed);
            }
        }
    };

    const ActiveTradingStatus = () => {
        const isConnected = connections[activeBuid]?.isConnected;
        const isActiveConnection = connections[activeBuid]?.isActiveConnection;
        const isMasterEnabled = masterTradeSettings.isMasterTradingEnabled;
        const isTradingActive = currentSettingsGroup.isTradingActive;
        const isTradingEnabled = currentSettingsGroup.isTradingEnabled;

        if (isTradingActive && isConnected && isActiveConnection && isMasterEnabled) {
            return (
                <div className="d-flex align-items-center">
                    <Badge bg="success">
                        Trading Is Active
                    </Badge>
                    {InfoHover("", "All conditions are met and trades are being executed for this group.")}
                </div>
            );
        }

        const hasAlgos = currentSettingsGroup.savedAlgos && currentSettingsGroup.savedAlgos.length > 0;
        const hasEnabledAccounts = currentSettingsGroup.accountsToTrade &&
            currentSettingsGroup.accountsToTrade.some(a => a.enableTrading);

        let reason = '';
        let hint = '';
        if (!isConnected) {
            reason = 'Brokerage connection is disconnected';
            hint = 'Your brokerage connection needs to be re-established. Go to the Connections page to reconnect.';
        } else if (!isActiveConnection) {
            reason = 'Outside trading hours';
            hint = 'The algorithms are not currently running. Trading will resume during the next trading session.';
        } else if (!isMasterEnabled) {
            reason = 'Master trading switch is off';
            hint = 'Turn on the Master On/Off Switch above to allow trading across all groups.';
        } else if (!hasAlgos) {
            reason = 'No algorithms selected';
            hint = 'Select at least one algorithm for this group.';
        } else if (!hasEnabledAccounts) {
            reason = 'No accounts enabled';
            hint = 'Enable at least one account to trade on for this group.';
        } else if (!isTradingEnabled) {
            reason = 'Trading is turned off for this group';
            hint = 'Toggle "Turn Trading On and Off" in the Trading Settings section below to enable trading for this group.';
        } else if (!isTradingActive) {
            reason = 'Not Active - Submit Changes';
            hint = 'Your group settings may need to be submitted. Click Submit to save and activate your configuration.';
        } else {
            reason = 'Trading is not active';
            hint = 'Check your connection, trading settings, and master switch to enable trading.';
        }

        const isOutsideHours = reason === 'Outside trading hours';

        return (
            <div className="d-flex align-items-center">
                <Badge bg={isOutsideHours ? '' : 'danger'} style={isOutsideHours ? { border: '2px solid #6c757d', color: '#6c757d' } : {}}>
                    {reason}
                </Badge>
                {InfoHover("", hint)}
            </div>
        );
    }

    const SetAlgoList = (groupId) => {
        if (settingsGroups && settingsGroups.length) {
            const savedAlgos = [];
            for (const group of settingsGroups) {
                if (group.groupName === groupId) {
                    for (const algo of group.savedAlgos) {
                        const splitId = algo.split('_');
                        const [name, ticker, timeframe] = splitId;
                        savedAlgos.push(
                            {
                                id: algo,
                                name,
                                ticker,
                                timeframe
                            }
                        )
                    }
                }
            }
            setSavedAlgos(sortAlgos(savedAlgos, ["timeframe", "ticker", "name"]));
        } else {
            setSavedAlgos([]);
        }
    }

    const SubmitTradeConfig = async (e) => {
        e.preventDefault();
        setShowRiskPreview(false);
        const token = await getAccessTokenSilently();

        for (const buid in fullTradeConfig) {
            if (connectionNeedsSave[buid] || buid === activeBuid) {
                const isSchwabConnection = connections[buid]?.broker === BROKERAGE_SCHWAB;
                let connectionTradeConfig = tradeConfig;
                let connectionSettingsGroups = settingsGroups;
                if (buid !== activeBuid) {
                    connectionTradeConfig = fullTradeConfig[buid];
                    connectionSettingsGroups = fullTradeConfig[buid].accountGroups;
                }
                const body = {
                    accountData: connectionTradeConfig.accountData.map((account) => {
                        return {
                            name: account.name,
                            amount: account.amount,
                            id: account.id,
                        }
                    }),
                    accountGroups: connectionSettingsGroups.map((group) => {
                        return {
                            accountsToTrade: activeBuid !== buid ? group.accountsToTrade : group.accountsToTrade.filter((a) => a.amount !== undefined && typeof a.amount === 'number' && a.enableTrading)
                                .map((account) => {
                                    return {
                                        name: account.name,
                                        enableTrading: account.enableTrading,
                                    }
                                }),
                            contractLimit: isSchwabConnection ? 999999 : group.contractLimit,
                            dailyLossLimit: isSchwabConnection ? 999999 : Number(group.dailyLossLimit),
                            dailyProfitLimit: isSchwabConnection ? 999999 : Number(group.dailyProfitLimit),
                            groupName: group.groupName,
                            isTradingEnabled: group.isTradingEnabled,
                            profitTarget: isSchwabConnection ? 999 : group.profitTarget,
                            riskSize: Number(group.riskSize),
                            maxRiskSize: Number(group.maxRiskSize),
                            riskMode: isSchwabConnection ? (group.riskMode || 'fixed') : 'fixed',
                            riskPercent: Number(group.riskPercent) || 0,
                            numContracts: Number(group.numContracts) || 0,
                            savedAlgos: group.savedAlgos,
                            stackTrades: group.stackTrades,
                            useTrailingStop: isSchwabConnection ? false : group.useTrailingStop,
                            trailingStopSettings: group.trailingStopSettings,
                            singleDirectionTrading: isSchwabConnection ? false : group.singleDirectionTrading,
                            tradingZones: isSchwabConnection ? [] : group.tradingZones,
                            maxTradesPerDay: group.maxTradesPerDay ? Number(group.maxTradesPerDay) : undefined,
                            mssFvgPullbackPercent: (isSchwabConnection || group.mssFvgPullbackPercent === undefined || group.mssFvgPullbackPercent === null || group.mssFvgPullbackPercent === '')
                                ? undefined
                                : Number(group.mssFvgPullbackPercent),
                            tradeWindowStart: (isSchwabConnection || !group.tradeWindowStart) ? undefined : group.tradeWindowStart,
                            tradeWindowEnd: (isSchwabConnection || !group.tradeWindowEnd) ? undefined : group.tradeWindowEnd,
                        }
                    })
                }
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };
                const params = {
                    uid: userId,
                    buid: buid,
                };
                try {
                    const newTradeConfig = await apiRequest('post', '/algo/demo/trade/config', '/api/v2/trade/config', {
                        headers, params, body,
                    });
                    if (buid === activeBuid) {
                        setSettingsGroups(
                            settingsGroups.map((group) => {
                                for (const tfGroup of newTradeConfig.data.accountGroups) {
                                    if (group.groupName === tfGroup.groupName) {
                                        return {
                                            ...group,
                                            isTradingActive: tfGroup.isTradingActive,
                                        };
                                    }
                                }
                                return group;
                            })
                        );
                    }
                    const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
                    needsSave[buid] = false;
                    setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
                } catch (error) {
                    console.log(error.message, error?.response?.data);
                    if (error && error.response && error.response.data) {
                        alert(`Error Submitting Form. ${error.response.data}`);
                    } else {
                        alert("Error Submitting Form. Try Again");
                    }
                    return error.message;
                }
            }
        }

        alert("Form Submitted Successfully");

    };

    const updateTradeConfig = async (body) => {
        const token = await getAccessTokenSilently();
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        const params = {
            uid: userId,
            buid: activeBuid,
        };
        try {
            await apiRequest('put', '/algo/demo/trade/config', '/api/v2/trade/config', {
                headers, params, body,
            });
        } catch (error) {
            console.log(error.message);
            if (error && error.response && error.response.data) {
                alert(`Error saving. ${error.response.data}`);
            } else {
                alert("Error saving. Try again");
            }
            return error.message;
        }

        alert("Saved Successfully");

    };

    const liquidate = async () => {
        const token = await getAccessTokenSilently();
        const headers = {
            'Authorization': `Bearer ${token}`,
        };
        const params = {
            uid: userId,
            buid: activeBuid,
        };

        try {
            await apiRequest('get', '/algo/liquidate', '/api/v2/algo/liquidate', {
                headers, params,
            });

            alert("Successfully liquidated");
        } catch (error) {
            console.log(error.message);
            alert("Error liquidating. Try Again");
            return error.message;
        }
    };

    const handleOpenTradingZoneModal = () => {
        setIsNewTradingZone(true);
        setEditTradingZone(undefined);
        setShowTradingZoneModal(true);
    }

    const handleOpenTradingZoneEditModal = (zone) => {
        setIsNewTradingZone(false);
        // If this zone is part of a POI-both pair, reconstruct it as a single poi-both zone
        if (zone.poiBothGroupId) {
            setEditTradingZone({
                ...zone,
                type: 'poi-both',
                direction: 'both',
            });
        } else {
            setEditTradingZone(zone);
        }
        setShowTradingZoneModal(true);
    }

    const handleAddASettingsGroup = () => {
        setShowModal(true);
        setIsNewGroup(true);
    };

    const handleEditSettingsGroupName = () => {
        setShowModal(true);
        setIsNewGroup(false);
    };

    const handleDeleteGroup = (groupName) => {
        const filteredGroups = settingsGroups.filter(
            (group) => group.groupName !== groupName
        );
        setSettingsGroups(filteredGroups);
        setSelectedGroupId(filteredGroups[0].groupName);
        SetAlgoList(filteredGroups[0].groupName);
    };

    const handleGroupSelection = (event) => {
        const { target } = event;
        const selectedGroupId = target.value;
        setSelectedGroupId(selectedGroupId);
        SetAlgoList(selectedGroupId);
        setSelectedAlgo({});
        setSelectedTicker({});
        setShowRiskDisclosure(false);
    };

    const AccountsToTrade = ({ accounts }) => {
        let accountsList;
        if (accounts) {
            accounts.sort((a, b) => {
                if (a.name < b.name) {
                    return 1;
                }
                if (a.name > b.name) {
                    return -1;
                }
                return 0;
            });
            return (
                <Table striped bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Account</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            accounts.filter((a) => a.amount !== undefined && typeof a.amount === 'number')
                                .map((account) => {
                                    return (
                                        <tr key={account.name}>
                                            <td>
                                                <Form.Check
                                                    disabled={!account.isAuthorized}
                                                    type="switch"
                                                    id={account.name}
                                                    label={account.name}
                                                    name={account.name}
                                                    checked={account.enableTrading}
                                                    onChange={handleAccountToTradeChange}
                                                    value={account.enableTrading}
                                                />
                                            </td>
                                            <td>${Math.round(account.amount * 100) / 100}</td>
                                        </tr>
                                    );
                                })
                        }
                    </tbody>
                </Table>
            )

        } else {
            accountsList = <Form.Check></Form.Check>;
        }

        return accountsList;
    };

    const TradeTable = () => {
        const isSchwabConnection = connections[activeBuid]?.broker === BROKERAGE_SCHWAB;
        let wins = 0;
        let losses = 0;
        let profit = 0;
        const testArray = tradeLog ?? [];
        for (let trade of testArray) {
            let pricePerPoint;
            if (trade.ticker) {
                const symbolConfig = config.symbols[trade.ticker] ?? config.symbols[trade.ticker.substring(0, trade.ticker.length - 2)];
                if (!symbolConfig) continue;
                pricePerPoint = symbolConfig.pricePerPoint;
            } else {
                pricePerPoint = config.symbols.ES.pricePerPoint;
            }
            if (trade.status === 'Win' && trade.filled) {
                wins = wins + 1;
                if (trade.entryStyle === 'sma' && trade.lastPrice) {
                    profit = profit + Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * Math.abs(trade.lastPrice - trade.fillPrice);
                } else {
                    profit = profit + Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * trade.profitTarget;
                }

            } else if (trade.status === 'Loss') {
                losses = losses + 1;
                if (trade.entryStyle === 'sma' && trade.lastPrice) {
                    profit = profit - Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * Math.abs(trade.lastPrice - trade.fillPrice);
                } else {
                    profit = profit - Math.round(currentSettingsGroup.riskSize / trade.stopSize / pricePerPoint) * pricePerPoint * trade.stopSize;
                }

            }
        }

        return (
            <div>
                {/* <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Total Trades</th>
                            <th>Total Trades Filled</th>
                            <th>Wins</th>
                            <th>Losses</th>
                            <th>Win Percentage</th>
                            <th>Estimated Profit</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{tradeLog.length ?? 0}</td>
                            <td>{wins + losses}</td>
                            <td>{wins}</td>
                            <td>{losses}</td>
                            <td>{(wins / (wins + losses) * 100) || 0}%</td>
                            <td>${Math.round(profit) || 0}</td>
                        </tr>
                    </tbody>
                </Table> */}
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Detection Time</th>
                            {/* <th>Filled Time</th> */}
                            {/* <th>End Time</th> */}
                            <th>Trade Type</th>
                            {!isSchwabConnection && <th>Fill Price</th>}
                            {!isSchwabConnection && <th>Stop Size</th>}
                            {!isSchwabConnection && <th>Profit Target</th>}
                            {/* <th>Trade Status</th> */}
                            <th>Algorithm</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            tradeLog ? tradeLog.map(trade => {
                                return (
                                    <tr key={trade.timestamp + trade.fillPrice}>
                                        <td>{new Date(trade.timestamp).toString()}</td>
                                        {/* <td>{trade.filledTimestamp ? new Date(trade.filledTimestamp).toString() : 'N/A'}</td> */}
                                        {/* <td>{trade.endTimestamp ? new Date(trade.endTimestamp).toString() : 'N/A'}</td> */}
                                        <td>{trade.type.includes('BUY') ? 'BUY' : 'SELL'}</td>
                                        {!isSchwabConnection && <td>{Math.round(trade.fillPrice * 100) / 100}</td>}
                                        {!isSchwabConnection && <td>{Math.round(trade.stopSize * 100) / 100}</td>}
                                        {!isSchwabConnection && <td>{Math.round(trade.profitTarget * 100) / 100}</td>}
                                        {/* <td>{trade.status}</td> */}
                                        <td>{trade.algoName + ' - ' + trade.ticker + ' - ' + trade.timeframe}</td>
                                    </tr>
                                );
                            }) : []
                        }
                    </tbody>
                </Table>
            </div>
        )
    };

    const AccountDropdown = () => {
        return (
            <Dropdown as={ButtonGroup} size="sm">
                <Dropdown.Toggle variant="primary" id="dropdown-basic">
                    {connections[activeBuid]?.name ? connections[activeBuid]?.name + ' ' : ''}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {
                        accountStatusList && accountStatusList.length ? accountStatusList.map((connection, index) => {
                            return (
                                <Fragment key={connection.id}>
                                    {index > 0 && <Dropdown.Divider />}
                                    <Dropdown.Item
                                        href={`/trade/${connection.id}`}
                                    >
                                        {connection.name}
                                    </Dropdown.Item>
                                </Fragment>
                            );
                        }) : null
                    }
                </Dropdown.Menu>
            </Dropdown>
        )
    };

    const addConnectionTradingZones = (tradeConfigGroups, groupList, tradingZone, buid) => {
        let isSaved = false;
        const updatedSettingsGroups = tradeConfigGroups.map((group) => {
            if (
                groupList.findIndex((g) =>
                    g.groupName === group.groupName &&
                    g.buid === buid &&
                    g.isCopiedToGroup
                ) >= 0
            ) {
                if (!group.tradingZones) {
                    group.tradingZones = [];
                }
                if (group.tradingZones.filter((zone) => zone.id === tradingZone.id).length === 0) {
                    group.tradingZones.push(tradingZone);
                    isSaved = true;
                } else {
                    alert(`Trading zone already exists for group ${group.groupName}`);
                }

                return group;
            }
            return group;
        });
        if (isSaved) {
            const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
            needsSave[buid] = true;
            setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
        }

        return updatedSettingsGroups;
    }

    const editConnectionTradingZones = (tradeConfigGroups, groupList, tradingZone, buid) => {
        const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
        const updatedSettingsGroups = tradeConfigGroups.map((group) => {
            const groupListIndex = groupList.findIndex((g) =>
                g.groupName === group.groupName &&
                g.buid === buid
            );
            if (groupListIndex >= 0) {
                if (!group.tradingZones) {
                    group.tradingZones = [];
                }
                const editZoneIndex = group.tradingZones.findIndex((zone) => zone.id === tradingZone.id);
                if (editZoneIndex >= 0) {
                    //If it's still in the group
                    if (groupList[groupListIndex].isCopiedToGroup) {
                        group.tradingZones[editZoneIndex] = tradingZone;
                        needsSave[buid] = true;

                    } else {
                        //If it's no longer in the group remove the zone
                        group.tradingZones.splice(editZoneIndex, 1);
                        needsSave[buid] = true;
                    }
                } else if (editZoneIndex < 0 && groupList[groupListIndex].isCopiedToGroup) {
                    //Add the zone to the group
                    group.tradingZones.push(tradingZone);
                    needsSave[buid] = true;

                }
                return group;
            }
            return group;
        });
        setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
        return updatedSettingsGroups;
    }

    const handleAddTradingZone = (tradingZones, groupList) => {
        setIsNewTradingZone(true);
        let validZone = true;
        const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));
        if (tradingZones && tradingZones.length) {
            for (const tradingZone of tradingZones) {
                tradingZone.id = new Date().getTime() + Math.random() * 1000;
                if (tradingZone.min && tradingZone.max) {
                    tradingZone.min = parseFloat(tradingZone.min);
                    tradingZone.max = parseFloat(tradingZone.max);
                    tradingZone.createdDate = new Date();
                }
                //Validation of the zone
                const year = new Date().getFullYear();
                const month = new Date().getMonth() + 1;
                const day = new Date().getDate();
                const normalizedDateString = `${month}/${day}/${year}`;
                if (
                    tradingZone.min &&
                    tradingZone.max &&
                    tradingZone.type &&
                    tradingZone.ticker &&
                    tradingZone.min < tradingZone.max &&
                    tradingZone.startTime &&
                    tradingZone.endTime &&
                    new Date(normalizedDateString + ' ' + tradingZone.startTime).getTime() < new Date(normalizedDateString + ' ' + tradingZone.endTime).getTime() &&
                    groupList.filter((group) => group.isCopiedToGroup).length
                ) {
                    for (const buid in allTradeConfig) {
                        //Update each connection's groups' trading zones
                        if (
                            allTradeConfig[buid] &&
                            allTradeConfig[buid].accountGroups &&
                            allTradeConfig[buid].accountGroups.length
                        ) {
                            if (buid === activeBuid) {
                                //For the activeBuid use settingsGroups
                                allTradeConfig[buid].accountGroups = addConnectionTradingZones(settingsGroups, groupList, tradingZone, buid);
                            } else {
                                //For other connections use the allTradeConfig
                                allTradeConfig[buid].accountGroups = addConnectionTradingZones(allTradeConfig[buid].accountGroups, groupList, tradingZone, buid);
                            }
                        }
                    }
                } else {
                    validZone = false;
                    alert('Invalid trading zone. All inputs besides the label are required, the minimum price has to be less than the maximum price, start time has to be before the end time, and at least one group has to be selected.');
                }
            }
            setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
            setFullTradeConfig(allTradeConfig);
            //setTradeConfig(allTradeConfig[activeBuid]);
        }

        if (validZone) {
            setShowTradingZoneModal(false);
        }
    };

    const handleEditTradingZone = (tradingZones, groupList, originalZone) => {
        if (!tradingZones || !tradingZones.length) return;

        // Validate all zones first
        for (const tradingZone of tradingZones) {
            if (tradingZone.min && tradingZone.max) {
                tradingZone.min = parseFloat(tradingZone.min);
                tradingZone.max = parseFloat(tradingZone.max);
            }
            const year = new Date().getFullYear();
            const month = new Date().getMonth() + 1;
            const day = new Date().getDate();
            const normalizedDateString = `${month}/${day}/${year}`;
            if (
                !(tradingZone.min &&
                tradingZone.max &&
                tradingZone.type &&
                tradingZone.ticker &&
                tradingZone.min < tradingZone.max &&
                tradingZone.startTime &&
                tradingZone.endTime &&
                new Date(normalizedDateString + ' ' + tradingZone.startTime).getTime() < new Date(normalizedDateString + ' ' + tradingZone.endTime).getTime() &&
                groupList.filter((group) => group.isCopiedToGroup).length)
            ) {
                alert('Invalid trading zone. All inputs besides the label are required, the minimum price has to be less than the maximum price, start time has to be before the end time, and at least one group has to be selected.');
                return;
            }
        }

        // For POI edits: delete old zone(s) then add new zone(s) in one pass
        if (originalZone && originalZone.isPOI) {
            const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));

            // Step 1: Collect IDs to delete
            const idsToDelete = new Set([originalZone.id]);
            if (originalZone.poiBothGroupId) {
                for (const buid in allTradeConfig) {
                    if (allTradeConfig[buid]?.accountGroups) {
                        for (const group of allTradeConfig[buid].accountGroups) {
                            if (group.tradingZones) {
                                for (const zone of group.tradingZones) {
                                    if (zone.poiBothGroupId === originalZone.poiBothGroupId) {
                                        idsToDelete.add(zone.id);
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Step 2: Remove old zones from all groups
            for (const buid in allTradeConfig) {
                if (allTradeConfig[buid]?.accountGroups) {
                    let groups = allTradeConfig[buid].accountGroups;
                    if (buid === activeBuid) {
                        groups = JSON.parse(JSON.stringify(settingsGroups));
                    }
                    for (const group of groups) {
                        if (group.tradingZones) {
                            group.tradingZones = group.tradingZones.filter(z => !idsToDelete.has(z.id));
                        }
                    }
                    allTradeConfig[buid].accountGroups = groups;
                }
            }

            // Step 3: Add new zones to selected groups
            for (const tradingZone of tradingZones) {
                tradingZone.id = new Date().getTime() + Math.random() * 1000;
                tradingZone.createdDate = new Date();
                for (const buid in allTradeConfig) {
                    if (allTradeConfig[buid]?.accountGroups) {
                        for (const group of allTradeConfig[buid].accountGroups) {
                            const groupInList = groupList.find(g => g.groupName === group.groupName && g.buid === buid);
                            if (groupInList?.isCopiedToGroup) {
                                if (!group.tradingZones) group.tradingZones = [];
                                group.tradingZones.push(tradingZone);
                            }
                        }
                    }
                }
            }

            setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
            setFullTradeConfig(allTradeConfig);
            setShowTradingZoneModal(false);
        } else {
            // Normal zone edit — use the original edit flow
            const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));
            for (const tradingZone of tradingZones) {
                for (const buid in allTradeConfig) {
                    if (
                        allTradeConfig[buid] &&
                        allTradeConfig[buid].accountGroups &&
                        allTradeConfig[buid].accountGroups.length
                    ) {
                        if (buid === activeBuid) {
                            allTradeConfig[buid].accountGroups = editConnectionTradingZones(settingsGroups, groupList, tradingZone, buid);
                        } else {
                            allTradeConfig[buid].accountGroups = editConnectionTradingZones(allTradeConfig[buid].accountGroups, groupList, tradingZone, buid);
                        }
                    }
                }
            }
            setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
            setFullTradeConfig(allTradeConfig);
            setShowTradingZoneModal(false);
        }
    };

    const handleDeleteTradingZone = (tradingZone) => {
        const allTradeConfig = JSON.parse(JSON.stringify(fullTradeConfig));
        const needsSave = JSON.parse(JSON.stringify(connectionNeedsSave));
        for (const buid in allTradeConfig) {
            //Update each connection's groups' trading zones
            if (
                allTradeConfig[buid] &&
                allTradeConfig[buid].accountGroups &&
                allTradeConfig[buid].accountGroups.length
            ) {
                let accountGroups = allTradeConfig[buid].accountGroups;
                if (buid === activeBuid) {
                    //For the activeBuid use settingsGroups
                    accountGroups = settingsGroups;
                }
                for (const group of accountGroups) {
                    if (group.tradingZones && group.tradingZones.length) {
                        //Filter out the trading zone being deleted
                        group.tradingZones = group.tradingZones
                            .filter((zone) => zone.id !== tradingZone.id);

                        needsSave[buid] = true;
                    }
                }
                allTradeConfig[buid].accountGroups = accountGroups;
            }
        }
        setConnectionNeedsSave(connectionNeedsSave => ({ ...connectionNeedsSave, ...needsSave }));
        setSettingsGroups(allTradeConfig[activeBuid].accountGroups);
        setFullTradeConfig(allTradeConfig);
        //setTradeConfig(allTradeConfig[activeBuid]);
    };

    return (
        <>
            {
                isAuthorized('tradeScreen') && (
                    <>
                        {showModal && (
                            <GroupNameModal
                                isNewGroup={isNewGroup}
                                showModal={showModal}
                                setShowModal={setShowModal}
                                handleSaveGroupName={handleSaveGroupName}
                                initialGroupName={isNewGroup ? "" : currentSettingsGroup.groupName}
                                groups={settingsGroups}
                            />
                        )}
                        <TemplateConfirmModal
                            show={showTemplateConfirm}
                            templateName={pendingTemplate}
                            preview={pendingTemplate ? buildTemplatePreview(pendingTemplate) : []}
                            isMultiGroup={TRADE_TEMPLATES.find(t => t.name === pendingTemplate)?.type === 'multi-group'}
                            usesXspFallback={(() => {
                                const t = TRADE_TEMPLATES.find(t => t.name === pendingTemplate);
                                const groups = t?.groups || (t?.settings ? [{ settings: t.settings }] : []);
                                return groups.some((g) => {
                                    const algos = g.settings?.savedAlgos || [];
                                    return algos.some(a => a.includes('_SPX_')) && algos.some(a => a.includes('_XSP_'));
                                });
                            })()}
                            onConfirm={handleApplyTemplate}
                            onCancel={() => setShowTemplateConfirm(false)}
                        />
                        {showTradingZoneModal && (
                            <TradingZoneModal
                                isNewTradingZone={isNewTradingZone}
                                showModal={showTradingZoneModal}
                                setShowModal={setShowTradingZoneModal}
                                handleTradingZone={isNewTradingZone ? handleAddTradingZone : handleEditTradingZone}
                                existingTradingZone={editTradingZone}
                                tradeConfig={fullTradeConfig}
                                connections={connections}
                                userId={userId}
                            />
                        )}
                        {
                            !settingsGroups.length ? (
                                isLoading ? (
                                    <LoadingScreen />
                                ) : (
                                    <Container className="mt-5 text-center">
                                        <p>No trading accounts found. Please go to <Link to="/connect">Connect</Link> to set up your connection.</p>
                                    </Container>
                                )
                            ) : (
                                <div>
                                    <Container >
                                        <Row>
                                            <Col>
                                            </Col>
                                            <Col xs={10} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left">
                                                <Row className="align-items-center border-bottom mb-2 pb-2 gy-2">
                                                    <Col xs={12} md="auto" className="d-flex align-items-center gap-2">
                                                        <h3 className="mb-0">{connections[activeBuid]?.isConnected ? 'Connection:' : ''} <AccountDropdown /></h3>
                                                    </Col>
                                                    <Col xs={12} md className="d-flex justify-content-md-end">
                                                        <Form.Group>
                                                            <Form.Check
                                                                size="lg"
                                                                id="isMasterTradingEnabled"
                                                                type="switch"
                                                                label={InfoHover(
                                                                    "Master On/Off Switch",
                                                                    "When this setting is turned on, any groups that have trading activated will be allowed to execute orders. When it's off, all trading will be turned off across all connections and all groups."
                                                                )}
                                                                key="isMasterTradingEnabled"
                                                                name="isMasterTradingEnabled"
                                                                checked={masterTradeSettings.isMasterTradingEnabled}
                                                                onChange={handleMasterTradingCheckboxChange}
                                                                value={masterTradeSettings.isMasterTradingEnabled}
                                                            />
                                                            <Form.Check
                                                                size="lg"
                                                                id="isMasterAutomaticallyEnabled"
                                                                type="switch"
                                                                label={InfoHover(
                                                                    "Enable Tomorrow",
                                                                    "When activated, the Master On/Off Switch will turn on automatically at the beginning of each trading day."
                                                                )}
                                                                key="isMasterAutomaticallyEnabled"
                                                                name="isMasterAutomaticallyEnabled"
                                                                checked={masterTradeSettings.isMasterAutomaticallyEnabled}
                                                                onChange={handleMasterTradingCheckboxChange}
                                                                value={masterTradeSettings.isMasterAutomaticallyEnabled}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                                <Row className="border-bottom mb-2 pb-2 gy-2 align-items-center">
                                                    <Col xs={12} md="auto" className="d-flex align-items-center">
                                                        <Form.Label className="mb-0 me-2">
                                                            <h5 style={{ margin: 0 }}>Group:</h5>
                                                        </Form.Label>
                                                        <Form.Group
                                                            controlId="group-select"
                                                            style={{ minWidth: "200px", flex: 1 }}
                                                        >
                                                            <Form.Select
                                                                onChange={handleGroupSelection}
                                                                value={selectedGroupId}
                                                            >
                                                                {settingsGroups.map((group) => (
                                                                    <option key={group.groupName} value={group.groupName}>
                                                                        {group.groupName}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xs="auto" className="d-flex align-items-center gap-2">
                                                        <Dropdown>
                                                            <Dropdown.Toggle variant="primary" id="dropdown-basic" size="sm">
                                                                Group Actions
                                                            </Dropdown.Toggle>

                                                            <Dropdown.Menu>
                                                                <Dropdown.Item
                                                                    key="add-group"
                                                                    variant="success"
                                                                    onClick={() => handleAddASettingsGroup()}
                                                                >
                                                                    Create A Group
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    key="edit-group"
                                                                    variant="secondary"
                                                                    onClick={handleEditSettingsGroupName}>
                                                                    Edit Group Name
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    key="delete-group"
                                                                    disabled={settingsGroups.length < 2}
                                                                    variant="outline-danger"
                                                                    ref={target}
                                                                    onClick={() =>
                                                                        handleDeleteGroup(currentSettingsGroup.groupName)
                                                                    }
                                                                >
                                                                    Delete Group
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                        {(() => {
                                                            const algoNames = algoOptions.map((a) => a.algoName.toLowerCase());
                                                            const checkAlgosAvailable = (savedAlgos) => {
                                                                return savedAlgos && savedAlgos.length > 0 && savedAlgos.every((savedAlgo) => {
                                                                    const algoName = savedAlgo.split('_')[0].toLowerCase();
                                                                    return algoNames.includes(algoName);
                                                                });
                                                            };
                                                            const availableTemplates = TRADE_TEMPLATES.filter((template) => {
                                                                if (template.type === 'multi-group') {
                                                                    return template.groups.every((group) => {
                                                                        if (group.isReference) {
                                                                            const ref = TRADE_TEMPLATES.find(t => t.name === group.templateName);
                                                                            return ref && checkAlgosAvailable(ref.settings?.savedAlgos);
                                                                        }
                                                                        return checkAlgosAvailable(group.settings?.savedAlgos);
                                                                    });
                                                                }
                                                                if (template.settings?.isReference) {
                                                                    const ref = TRADE_TEMPLATES.find(t => t.name === template.settings.templateName);
                                                                    return ref && checkAlgosAvailable(ref.settings?.savedAlgos);
                                                                }
                                                                return checkAlgosAvailable(template.settings?.savedAlgos);
                                                            });
                                                            if (availableTemplates.length === 0) return null;
                                                            return (
                                                                <Dropdown>
                                                                    <Dropdown.Toggle variant="outline-secondary" id="template-dropdown" size="sm">
                                                                        Templates
                                                                    </Dropdown.Toggle>
                                                                    <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                                        {availableTemplates.map((template, index) => (
                                                                            <Fragment key={template.name}>
                                                                                {index > 0 && <Dropdown.Divider />}
                                                                                <Dropdown.Item
                                                                                    onClick={() => handleTemplateClick(template.name)}
                                                                                >
                                                                                    {template.name}
                                                                                </Dropdown.Item>
                                                                            </Fragment>
                                                                        ))}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            );
                                                        })()}
                                                    </Col>
                                                </Row>
                                                <Row className="border-bottom mb-2 pb-2 align-items-center">
                                                    <Col xs="auto" className="d-flex align-items-center gap-2">
                                                        <span className="fw-semibold">Group Status:</span>
                                                        <ActiveTradingStatus />
                                                    </Col>
                                                </Row>
                                                {
                                                    connections[activeBuid]?.isConnected && (
                                                        <Form className="bg-light">
                                                            <Row className="mb-3 pb-2 border-bottom">
                                                                <Col>
                                                                    <Accordion>
                                                                        <Accordion.Item eventKey="0">
                                                                            <Accordion.Header>
                                                                                <Form.Label>
                                                                                    {InfoHover(
                                                                                        "Select Algorithms",
                                                                                        "Select the algorithm(s) you want to take trades on. Talk to your licensor for more details on each algorithm."
                                                                                    )}
                                                                                </Form.Label>
                                                                            </Accordion.Header>
                                                                            <Accordion.Body>
                                                                                <Container
                                                                                    className="d-flex justify-content-center"
                                                                                    style={{ padding: "0" }}
                                                                                >
                                                                                    <Container style={{ padding: "2px" }}>
                                                                                        <h5>Algorithm</h5>
                                                                                        <ListGroup
                                                                                            id="algos"
                                                                                            style={{
                                                                                                height: "200px",
                                                                                                overflowY: "auto",
                                                                                                borderRadius: "0",
                                                                                                border: "1px solid lightgray",
                                                                                            }}
                                                                                        >
                                                                                            {algoOptions.map((algo) => (
                                                                                                <ListGroup.Item
                                                                                                    key={algo.algoName}
                                                                                                    active={
                                                                                                        selectedAlgo.algoName === algo.algoName
                                                                                                    }
                                                                                                    style={{
                                                                                                        borderLeft: "none",
                                                                                                        borderRight: "none",
                                                                                                        borderRadius: "0",
                                                                                                        cursor: "pointer",
                                                                                                    }}
                                                                                                    onClick={() =>
                                                                                                        handleAlgoSelection(algo.algoName)
                                                                                                    }
                                                                                                >
                                                                                                    {algo.testing ? algo.algoName + ' BETA' : algo.algoName}
                                                                                                </ListGroup.Item>
                                                                                            ))}
                                                                                        </ListGroup>
                                                                                    </Container>
                                                                                    <Container style={{ padding: "2px" }}>
                                                                                        <h5>Ticker</h5>
                                                                                        <ListGroup
                                                                                            id="tickers"
                                                                                            style={{
                                                                                                height: "200px",
                                                                                                overflowY: "auto",
                                                                                                borderRadius: "0",
                                                                                                border: "1px solid lightgray",
                                                                                            }}
                                                                                        >
                                                                                            {selectedAlgo.tickers &&
                                                                                                Object.keys(selectedAlgo.tickers).map(
                                                                                                    (tickerName) => {
                                                                                                        return (
                                                                                                            <ListGroup.Item
                                                                                                                key={tickerName}
                                                                                                                active={
                                                                                                                    selectedTicker.tickerName ===
                                                                                                                    tickerName
                                                                                                                }
                                                                                                                style={{
                                                                                                                    borderLeft: "none",
                                                                                                                    borderRight: "none",
                                                                                                                    borderRadius: "0",
                                                                                                                    cursor: "pointer",
                                                                                                                }}
                                                                                                                onClick={() =>
                                                                                                                    handleTickerSelection(tickerName)
                                                                                                                }
                                                                                                            >
                                                                                                                {tickerName}
                                                                                                            </ListGroup.Item>
                                                                                                        );
                                                                                                    }
                                                                                                )}
                                                                                        </ListGroup>
                                                                                    </Container>
                                                                                    <Container style={{ padding: "2px" }}>
                                                                                        <h5>Time Frame</h5>
                                                                                        <ListGroup
                                                                                            id="time-frames"
                                                                                            style={{
                                                                                                height: "200px",
                                                                                                overflowY: "auto",
                                                                                                borderRadius: "0",
                                                                                                border: "1px solid lightgray",
                                                                                            }}
                                                                                        >
                                                                                            {selectedTicker.timeframes &&
                                                                                                selectedTicker.timeframes.map((timeframe) => {
                                                                                                    const id = `${selectedAlgo.algoName}_${selectedTicker.tickerName}_${timeframe}`;
                                                                                                    const foundAlgo = savedAlgos.find(
                                                                                                        (algo) => algo.id === id
                                                                                                    );
                                                                                                    return (
                                                                                                        <Fragment key={id}>
                                                                                                            {!foundAlgo ? (
                                                                                                                <ListGroup.Item
                                                                                                                    style={{
                                                                                                                        display: "flex",
                                                                                                                        justifyContent: "space-between",
                                                                                                                        borderLeft: "none",
                                                                                                                        borderRight: "none",
                                                                                                                        borderRadius: "0",
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {timeframe}
                                                                                                                    <Button
                                                                                                                        variant="outline-primary"
                                                                                                                        size="sm"
                                                                                                                        onClick={() =>
                                                                                                                            handleTimeFrameSelection(
                                                                                                                                timeframe
                                                                                                                            )
                                                                                                                        }
                                                                                                                    >
                                                                                                                        +
                                                                                                                    </Button>
                                                                                                                </ListGroup.Item>
                                                                                                            ) : (
                                                                                                                <></>
                                                                                                            )}
                                                                                                        </Fragment>
                                                                                                    );
                                                                                                })}
                                                                                        </ListGroup>
                                                                                    </Container>
                                                                                </Container>
                                                                                <Container style={{ padding: "16px 0" }}>
                                                                                    <h5>Saved Algorithms</h5>
                                                                                    <div
                                                                                        style={{
                                                                                            height: "300px",
                                                                                            overflowY: "auto",
                                                                                            border: "1px solid #ccc",
                                                                                            background: "white",
                                                                                            borderRadius: "5px",
                                                                                            padding: "5px",
                                                                                        }}
                                                                                    >
                                                                                        <Table striped bordered hover>
                                                                                            <thead>
                                                                                                <tr>
                                                                                                    <th>Algorithm</th>
                                                                                                    <th>Ticker</th>
                                                                                                    <th>Time Frame</th>
                                                                                                    <th>Actions</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {savedAlgos.map((algo) => {
                                                                                                    return (
                                                                                                        <tr key={algo.name + algo.ticker + algo.timeframe}>
                                                                                                            <td>
                                                                                                                {" "}
                                                                                                                {algo.name}{" "}
                                                                                                                {algo.new ? (
                                                                                                                    <span
                                                                                                                        style={{
                                                                                                                            color: "green",
                                                                                                                            fontWeight: "bold",
                                                                                                                            fontSize: "10px",
                                                                                                                            verticalAlign: "top",
                                                                                                                        }}
                                                                                                                    >
                                                                                                                        NEW
                                                                                                                    </span>
                                                                                                                ) : (
                                                                                                                    <></>
                                                                                                                )}
                                                                                                            </td>
                                                                                                            <td>{algo.ticker}</td>
                                                                                                            <td>{algo.timeframe}</td>
                                                                                                            <td>
                                                                                                                <Button
                                                                                                                    variant="outline-danger"
                                                                                                                    size="sm"
                                                                                                                    onClick={() =>
                                                                                                                        handleRemoveSelection(algo)
                                                                                                                    }
                                                                                                                >
                                                                                                                    Remove
                                                                                                                </Button>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                    );
                                                                                                })}
                                                                                            </tbody>
                                                                                        </Table>
                                                                                    </div>
                                                                                </Container>
                                                                            </Accordion.Body>
                                                                        </Accordion.Item>
                                                                    </Accordion>
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-3 border-bottom">
                                                                <Col>
                                                                    <Form.Group className="mb-3 pb-2 border-bottom" controlId="tradeSettings">
                                                                        <Form.Label>Trading Settings</Form.Label>
                                                                        <Form.Check
                                                                            id="isTradingEnabled"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Turn Trading On and Off",
                                                                                "When this setting is turned on, new trades will be executed against the accounts you have toggled on if you are in a trading time window. Toggle this switch off to turn trading off."
                                                                            )}
                                                                            key="isTradingEnabled"
                                                                            name="isTradingEnabled"
                                                                            checked={currentSettingsGroup.isTradingEnabled}
                                                                            onChange={(e) => {
                                                                                handleCheckboxChange(e);
                                                                                if (e.target.checked) {
                                                                                    setShowRiskDisclosure(true);
                                                                                }
                                                                            }}
                                                                            value={currentSettingsGroup.isTradingEnabled}
                                                                        />
                                                                        {showRiskDisclosure && (
                                                                            <Alert
                                                                                variant="warning"
                                                                                dismissible
                                                                                onClose={() => setShowRiskDisclosure(false)}
                                                                                className="mt-2 mb-2"
                                                                            >
                                                                                <Alert.Heading as="h6" className="mb-1">
                                                                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                                                    Risk Disclosure
                                                                                </Alert.Heading>
                                                                                <p className="mb-0 small">
                                                                                    By turning trading on, you acknowledge and agree to our{' '}
                                                                                    <Link to="/legal/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</Link>{' '}
                                                                                    and risk disclosures. Trading securities, options, futures, and other
                                                                                    derivatives involves substantial risk of loss and is not suitable for
                                                                                    every investor. Automated trading can increase risk. You are solely
                                                                                    responsible for monitoring your account and can lose some, all, or in
                                                                                    some cases more than the amount you invest. Past performance is not
                                                                                    indicative of future results.
                                                                                </p>
                                                                            </Alert>
                                                                        )}
                                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                                        <Form.Check
                                                                            id="stackTrades"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Stack Trades",
                                                                                "Stacking trades allows you to take another trade while a trade is currently working. If Stack Trades is turned off, you will be limited to 1 trade working at a time."
                                                                            )}
                                                                            key="stackTrades"
                                                                            name="stackTrades"
                                                                            checked={currentSettingsGroup.stackTrades}
                                                                            onChange={handleCheckboxChange}
                                                                            value={currentSettingsGroup.stackTrades}
                                                                        />
                                                                        )}
                                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                                        <Form.Check
                                                                            id="singleDirectionTrading"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Single Direction Trading",
                                                                                `
                                                                                    Single direction trading will prevent an opposing order from being placed if one is already filled. It disallows US indices from placing buy orders when a short position is filled and working and vice versa. It also disallows opposing Gold positions if a position is already filled and working. This setting applies to all accounts on a single connection.
                                                                                    For example, if an MES buy order is filled, and an algorithm detects an MNQ sell order, the MNQ sell order will not be taken. If a GC buy order is filled, and an algorithm detects a GC sell order, the GC sell order will not be taken.
                                                                                    Second, for custom zones, trades will only be executed from the most recently touched custom zone and any trades going into an opposing zone will be cancelled if they are not filled or profit target adjusted to the edge of the opposing zone when this setting is on.
                                                                                    For example, if price hits a custom demand zone you set, and by the time price hits a custom supply zone you set you have an unfilled buy order, the unfilled order will be cancelled.
                                                                                    Additionally, if you have a buy order with a profit target overlapping the custom supply zone you have set, the profit target will be adjusted to the minimum of the supply zone.
                                                                                `
                                                                            )}
                                                                            key="singleDirectionTrading"
                                                                            name="singleDirectionTrading"
                                                                            checked={currentSettingsGroup.singleDirectionTrading}
                                                                            onChange={handleCheckboxChange}
                                                                            value={currentSettingsGroup.singleDirectionTrading}
                                                                        />
                                                                        )}
                                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                                        <Form.Check
                                                                            id="useTrailingStop"
                                                                            type="switch"
                                                                            label={InfoHover(
                                                                                "Trailing Stop",
                                                                                "When toggled on, an order with an auto-trailing stop will be placed."
                                                                            )}
                                                                            key="useTrailingStop"
                                                                            name="useTrailingStop"
                                                                            checked={currentSettingsGroup.useTrailingStop}
                                                                            onChange={handleCheckboxChange}
                                                                            value={currentSettingsGroup.useTrailingStop}
                                                                        />
                                                                        )}
                                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB &&
                                                                            currentSettingsGroup.useTrailingStop &&
                                                                            <Accordion className="mt-2">
                                                                                <Accordion.Item eventKey="0">
                                                                                    <Accordion.Header>
                                                                                        <Form.Label>
                                                                                            {InfoHover(
                                                                                                "Trailing Stop Settings",
                                                                                                "Select how you want the trailing stop configured. CAUTION: Make sure you understand these settings fully before using them. It is suggested to place orders on a demo account with these settings before using them and understand fully how your broker uses a trailing stop. Stop Loss defines how close your trailing stop will trail price. Trigger defines how far price has to move before the trailnig stop begins. Frequency defines how often your stop loss moves."
                                                                                            )}
                                                                                        </Form.Label>
                                                                                    </Accordion.Header>
                                                                                    <Accordion.Body>
                                                                                        <Form.Group
                                                                                            className="mb-1 pb-1"
                                                                                            controlId="trailingStopSettings.stopSize"
                                                                                        >
                                                                                            <Form.Label>
                                                                                                {InfoHover(
                                                                                                    "Stop Size (multiple of trade stop size)",
                                                                                                    "How close your trailing stop will trail price. For example, if you set a value of 1, your stop loss will trail price by 1x the stop loss of your trade."
                                                                                                )}
                                                                                            </Form.Label>
                                                                                            <Form.Control
                                                                                                type="number"
                                                                                                placeholder="Stop Size"
                                                                                                value={currentSettingsGroup.trailingStopSettings?.stopSize}
                                                                                                name="trailingStopSettings.stopSize"
                                                                                                onChange={handleTrailingStopChange}
                                                                                                required
                                                                                            />
                                                                                        </Form.Group>
                                                                                        <Form.Group
                                                                                            className="mb-1 pb-1"
                                                                                            controlId="trailingStopSettings.trigger"
                                                                                        >
                                                                                            <Form.Label>
                                                                                                {InfoHover(
                                                                                                    "Trigger (multiple of trade profit target)",
                                                                                                    "Defines how far price has to move before the trailing stop begins based on your profit target. For example, if you set a value of 0.9, your trailing stop will kick in when price moves 90% of the way to your trade's profit target. This value should be between 0 and 1."
                                                                                                )}
                                                                                            </Form.Label>
                                                                                            <Form.Control
                                                                                                type="number"
                                                                                                placeholder="Trigger"
                                                                                                value={currentSettingsGroup.trailingStopSettings?.trigger}
                                                                                                name="trailingStopSettings.trigger"
                                                                                                onChange={handleTrailingStopChange}
                                                                                                required
                                                                                            />
                                                                                        </Form.Group>
                                                                                        <Form.Group
                                                                                            className="mb-1 pb-1"
                                                                                            controlId="trailingStopSettings,frequency"
                                                                                        >
                                                                                            <Form.Label>
                                                                                                {InfoHover(
                                                                                                    "Frequency (multiple of trade stop size)",
                                                                                                    "How often your stop loss moves. For example, if you set a value of 1, your trailing stop will move every time price moves 1x your trade's stop size."
                                                                                                )}
                                                                                            </Form.Label>
                                                                                            <Form.Control
                                                                                                type="number"
                                                                                                placeholder="Frequency"
                                                                                                value={currentSettingsGroup.trailingStopSettings?.frequency}
                                                                                                name="trailingStopSettings.frequency"
                                                                                                onChange={handleTrailingStopChange}
                                                                                                required
                                                                                            />
                                                                                        </Form.Group>
                                                                                    </Accordion.Body>
                                                                                </Accordion.Item>
                                                                            </Accordion>
                                                                        }
                                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && hasLastSonPullbackSelected && (
                                                                        <Form.Group className="mb-1 pb-1 mt-2" controlId="mssFvgPullbackPercent">
                                                                            <Form.Label>
                                                                                {InfoHover(
                                                                                    "Last Son Pullback Entry (%)",
                                                                                    "For Last Son trades taken on the 1m-pullback model, the limit entry is placed this % of the way from the lead-in fractal back toward the zone's price threshold (the stop). The stop sits at the threshold, so a larger % means a tighter stop and a better entry. Only applies when you've selected the Last Son 1m-pullback option for a ticker."
                                                                                )}
                                                                            </Form.Label>
                                                                            <div className="d-flex align-items-center">
                                                                                <Slider
                                                                                    className="w-100"
                                                                                    value={Number(currentSettingsGroup.mssFvgPullbackPercent) || 10}
                                                                                    min={10}
                                                                                    max={90}
                                                                                    step={0.1}
                                                                                    onChange={(n) => handleChange({ target: { name: 'mssFvgPullbackPercent', value: String(n) } })}
                                                                                />
                                                                                <span className="ms-2" style={{ minWidth: '4ch' }}>
                                                                                    {(Number(currentSettingsGroup.mssFvgPullbackPercent) || 10).toFixed(1)}%
                                                                                </span>
                                                                            </div>
                                                                        </Form.Group>
                                                                        )}
                                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                                        <Form.Group className="mb-1 pb-1 mt-2" controlId="tradeWindow">
                                                                            <Form.Label>
                                                                                {InfoHover(
                                                                                    "Time to Trade (Eastern Time)",
                                                                                    "Optional trading window in US Eastern time. When both a start and end time are set, new trades are only taken while the current Eastern time is within the window (start inclusive, end exclusive) — all other trade checks still apply. Leave either field blank to trade all day. The window must be same-day (end after start)."
                                                                                )}
                                                                            </Form.Label>
                                                                            <div className="d-flex align-items-center">
                                                                                <Form.Control
                                                                                    type="time"
                                                                                    value={currentSettingsGroup.tradeWindowStart || ''}
                                                                                    name="tradeWindowStart"
                                                                                    onChange={handleChange}
                                                                                    aria-label="Trade window start (Eastern Time)"
                                                                                />
                                                                                <span className="mx-2">to</span>
                                                                                <Form.Control
                                                                                    type="time"
                                                                                    value={currentSettingsGroup.tradeWindowEnd || ''}
                                                                                    name="tradeWindowEnd"
                                                                                    onChange={handleChange}
                                                                                    aria-label="Trade window end (Eastern Time)"
                                                                                />
                                                                            </div>
                                                                        </Form.Group>
                                                                        )}
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-3 pb-2" controlId="accountToTrade">
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Accounts to Trade On",
                                                                                "Choose which accounts to execute new trades on."
                                                                            )}
                                                                        </Form.Label>
                                                                        <div style={{
                                                                            minHeight: "200px",
                                                                            maxHeight: "500px",
                                                                            height: "275px",
                                                                            overflowY: "auto",
                                                                            resize: "vertical",
                                                                            border: '1px solid #ccc',
                                                                            background: 'white',
                                                                            borderRadius: '5px',
                                                                            padding: '5px',
                                                                        }}>
                                                                            <AccountsToTrade
                                                                                accounts={currentSettingsGroup.accountsToTrade}
                                                                            />
                                                                        </div>
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col className="border-right">
                                                                    {(() => {
                                                                        const isSchwab = connections[activeBuid]?.broker === BROKERAGE_SCHWAB;
                                                                        const isPercent = isSchwab && currentSettingsGroup.riskMode === 'percent';
                                                                        const isContracts = isSchwab && currentSettingsGroup.riskMode === 'contracts';
                                                                        const label = isContracts ? "Contracts" : "Average Risk Size";
                                                                        const tooltip = isContracts
                                                                            ? "Trade a fixed number of contracts per trade instead of sizing by risk. Toggle to $ or % to size by risk amount."
                                                                            : isPercent
                                                                                ? "Percent of each account's start-of-day balance to risk per trade. Computed per-account at trade time. Toggle to $ for a fixed dollar amount."
                                                                                : "The dollar amount to risk for each trade. This is the average risk size ALM Trading will attempt to achieve over time." + (isSchwab ? " Toggle to % or # to size by percent of balance or a fixed contract count." : "");
                                                                        const setRiskMode = (mode) => {
                                                                            handleChange({ target: { name: 'riskMode', value: mode } });
                                                                        };
                                                                        return (
                                                                            <Form.Group className="mb-1 pb-1" controlId={isPercent ? 'riskPercent' : isContracts ? 'numContracts' : 'riskSize'}>
                                                                                <Form.Label>
                                                                                    {InfoHover(label, tooltip)}
                                                                                </Form.Label>
                                                                                <InputGroup>
                                                                                    {isSchwab && (
                                                                                        <ButtonGroup>
                                                                                            <Button
                                                                                                type="button"
                                                                                                variant={!isPercent && !isContracts ? 'primary' : 'outline-secondary'}
                                                                                                onClick={() => setRiskMode('fixed')}
                                                                                                title="Fixed dollar amount per trade"
                                                                                                style={{ minWidth: '3.5rem', fontWeight: 600 }}
                                                                                            >
                                                                                                $
                                                                                            </Button>
                                                                                            <Button
                                                                                                type="button"
                                                                                                variant={isPercent ? 'primary' : 'outline-secondary'}
                                                                                                onClick={() => setRiskMode('percent')}
                                                                                                title="Percent of account balance"
                                                                                                style={{ minWidth: '3.5rem', fontWeight: 600 }}
                                                                                            >
                                                                                                %
                                                                                            </Button>
                                                                                            <Button
                                                                                                type="button"
                                                                                                variant={isContracts ? 'primary' : 'outline-secondary'}
                                                                                                onClick={() => setRiskMode('contracts')}
                                                                                                title="Fixed number of contracts per trade"
                                                                                                style={{ minWidth: '3.5rem', fontWeight: 600 }}
                                                                                            >
                                                                                                #
                                                                                            </Button>
                                                                                        </ButtonGroup>
                                                                                    )}
                                                                                    {!isSchwab && <InputGroup.Text>$</InputGroup.Text>}
                                                                                    {isContracts ? (
                                                                                        <Form.Control
                                                                                            type="number"
                                                                                            step="1"
                                                                                            min="1"
                                                                                            placeholder="Number of contracts per trade"
                                                                                            value={currentSettingsGroup.numContracts ?? 1}
                                                                                            name="numContracts"
                                                                                            onChange={(e) => {
                                                                                                const raw = e.target.value;
                                                                                                if (raw === '') {
                                                                                                    handleChange(e);
                                                                                                    return;
                                                                                                }
                                                                                                const n = Math.floor(Number(raw));
                                                                                                if (!Number.isFinite(n)) return;
                                                                                                handleChange({ target: { name: 'numContracts', value: String(Math.max(1, n)) } });
                                                                                            }}
                                                                                            required
                                                                                        />
                                                                                    ) : isPercent ? (
                                                                                        <Form.Control
                                                                                            ref={riskPreviewTargetRef}
                                                                                            type="number"
                                                                                            step="0.1"
                                                                                            min="0"
                                                                                            max="75"
                                                                                            placeholder="Percent of account to risk each trade (0-75)"
                                                                                            value={currentSettingsGroup.riskPercent ?? 1}
                                                                                            name="riskPercent"
                                                                                            onChange={(e) => {
                                                                                                const raw = e.target.value;
                                                                                                if (raw === '') {
                                                                                                    handleChange(e);
                                                                                                    setShowRiskPreview(true);
                                                                                                    return;
                                                                                                }
                                                                                                const n = Number(raw);
                                                                                                if (!Number.isFinite(n)) return;
                                                                                                const clamped = Math.max(0, Math.min(75, n));
                                                                                                handleChange({ target: { name: 'riskPercent', value: String(clamped) } });
                                                                                                setShowRiskPreview(true);
                                                                                            }}
                                                                                            required
                                                                                        />
                                                                                    ) : (
                                                                                        <Form.Control
                                                                                            type="number"
                                                                                            placeholder="Dollar amount to risk each trade on average"
                                                                                            value={currentSettingsGroup.riskSize}
                                                                                            name="riskSize"
                                                                                            onChange={handleChange}
                                                                                            required
                                                                                        />
                                                                                    )}
                                                                                    {isPercent && <InputGroup.Text>% of account</InputGroup.Text>}
                                                                                </InputGroup>
                                                                                {isPercent && (() => {
                                                                                    const pct = Number(currentSettingsGroup.riskPercent);
                                                                                    const accountsWithBalance = (currentSettingsGroup.accountsToTrade || [])
                                                                                        .filter(a => typeof a.amount === 'number' && a.amount > 0);
                                                                                    if (!Number.isFinite(pct) || pct <= 0 || accountsWithBalance.length === 0) return null;
                                                                                    return (
                                                                                        <Overlay target={riskPreviewTargetRef.current} show={showRiskPreview} placement="bottom">
                                                                                            <Popover id="risk-preview-popover" style={{ maxWidth: '20rem' }}>
                                                                                                <Popover.Header as="div" className="small d-flex justify-content-between align-items-center">
                                                                                                    <span>Estimated $ risk per trade</span>
                                                                                                    <button type="button" className="btn-close btn-close-sm" aria-label="Dismiss" onClick={() => setShowRiskPreview(false)} />
                                                                                                </Popover.Header>
                                                                                                <Popover.Body className="small py-2">
                                                                                                    <div className="text-muted mb-1">Based on last known balance. Dismisses on save.</div>
                                                                                                    <ul className="mb-0 ps-3">
                                                                                                        {accountsWithBalance.map(a => (
                                                                                                            <li key={a.name}>
                                                                                                                {a.name} — ${(a.amount * pct / 100).toFixed(2)} @ {pct}% of ${Math.round(a.amount).toLocaleString()}
                                                                                                            </li>
                                                                                                        ))}
                                                                                                    </ul>
                                                                                                </Popover.Body>
                                                                                            </Popover>
                                                                                        </Overlay>
                                                                                    );
                                                                                })()}
                                                                            </Form.Group>
                                                                        );
                                                                    })()}
                                                                    {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                                    <Form.Group
                                                                        className="mb-1 pb-1"
                                                                        controlId="maxRiskSize"
                                                                    >
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Max Risk Size",
                                                                                "The maximum dollar amount to risk on each trade. If you set this value and a trade exceeds this amount, the trade will not be executed on your account(s)."
                                                                            )}
                                                                        </Form.Label>
                                                                        <InputGroup>
                                                                            <InputGroup.Text>$</InputGroup.Text>
                                                                            <Form.Control
                                                                                type="number"
                                                                                placeholder="Maximum dollar amount to risk each trade"
                                                                                value={currentSettingsGroup.maxRiskSize}
                                                                                name="maxRiskSize"
                                                                                onChange={handleChange}
                                                                                required
                                                                            />
                                                                        </InputGroup>
                                                                    </Form.Group>
                                                                    )}
                                                                    {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                                    <>
                                                                    <Form.Group className="mb-1 pb-1" controlId="profitTarget">
                                                                        <Form.Label>{InfoHover('Profit Target',
                                                                            'This setting dictates your reward to risk ratio. Your profit target is a multiple of your stop size, so if you set a profit target of 2, your profit target will be 2x your stop size.')}
                                                                        </Form.Label>
                                                                        <InputGroup>
                                                                            <InputGroup.Text>#</InputGroup.Text>
                                                                            <Form.Control
                                                                                type="number"
                                                                                placeholder="Multiple of stop size"
                                                                                value={currentSettingsGroup.profitTarget}
                                                                                name="profitTarget"
                                                                                onChange={handleChange}
                                                                                required
                                                                            />
                                                                        </InputGroup>
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-1 pb-1" controlId="contractLimit">
                                                                        <Form.Label>{InfoHover('Contract Limit',
                                                                            'Specify the maximum number of contracts that will be open at any one time across all algorithms and all instruments. If the contract limit is reached for an account, no new orders will be placed for that account. If a new trade signal occurs that would exceed the contract limit, ALM Trading will use the remaining contracts left to reach the contract limit. For example, if you set a 10 contract limit, and you have 6 contracts open on ES, and a new order comes in for another 6 contracts, ALM Trading will place the new order at 4 contracts to fill in the remaining 10 contract limit.')}</Form.Label>
                                                                        <InputGroup>
                                                                            <InputGroup.Text>#</InputGroup.Text>
                                                                            <Form.Control
                                                                                type="number"
                                                                                placeholder="Max contracts open at once"
                                                                                value={currentSettingsGroup.contractLimit}
                                                                                name="contractLimit"
                                                                                onChange={handleChange}
                                                                                required
                                                                            />
                                                                        </InputGroup>
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-1 pb-1" controlId="dailyLossLimit">
                                                                        <Form.Label>{InfoHover('Daily Loss Limit',
                                                                            'Specify the maximum daily loss limit. This value is monitored in real time, and if reached, orders on your account will be liquidated and no new orders will be placed for that day.')}</Form.Label>
                                                                        <InputGroup>
                                                                            <InputGroup.Text>$</InputGroup.Text>
                                                                            <Form.Control
                                                                                type="number"
                                                                                placeholder="Daily loss limit"
                                                                                value={currentSettingsGroup.dailyLossLimit}
                                                                                name="dailyLossLimit"
                                                                                onChange={handleChange}
                                                                                required
                                                                            />
                                                                        </InputGroup>
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-1 pb-1" controlId="dailyProfitLimit">
                                                                        <Form.Label>{InfoHover('Daily Profit Limit',
                                                                            'Specify the maximum daily profit limit. This value is monitored in real time, and if reached, orders on your account will be liquidated and no new orders will be placed for that day.')}</Form.Label>
                                                                        <InputGroup>
                                                                            <InputGroup.Text>$</InputGroup.Text>
                                                                            <Form.Control
                                                                                type="number"
                                                                                placeholder="Daily profit limit"
                                                                                value={currentSettingsGroup.dailyProfitLimit}
                                                                                name="dailyProfitLimit"
                                                                                onChange={handleChange}
                                                                                required
                                                                            />
                                                                        </InputGroup>
                                                                    </Form.Group>
                                                                    <Form.Group className="mb-3 pb-3" controlId="maxTradesPerDay">
                                                                        <Form.Label>{InfoHover('Max Trades Per Day',
                                                                            'Specify the maximum number of filled trades allowed per day for this group. If the number of filled trades exceeds this limit, no new trades will be placed for the rest of the day for the group. Leave blank for unlimited trades. If you set this value to 2 for example, more than 2 trades can be placed, but the system will make a best attempt to only allow 2 trades to fill and will attempt to cancel other open trades after the first 2 fill.')}</Form.Label>
                                                                        <InputGroup>
                                                                            <InputGroup.Text>#</InputGroup.Text>
                                                                            <Form.Control
                                                                                type="number"
                                                                                placeholder="Optional — leave blank for unlimited"
                                                                                value={currentSettingsGroup.maxTradesPerDay}
                                                                                name="maxTradesPerDay"
                                                                                onChange={handleChange}
                                                                            />
                                                                        </InputGroup>
                                                                    </Form.Group>
                                                                    </>
                                                                    )}
                                                                </Col>
                                                            </Row>
                                                            {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                            <Row className="mb-3 border-bottom">
                                                                <Col>
                                                                    <Form.Group className="mb-3" controlId="tradingZones">
                                                                        <Form.Label>
                                                                            {InfoHover(
                                                                                "Custom Trading Zones",
                                                                                "For Krypton, you can set custom price ranges where Krypton will execute trades. If any trading zones are set, it will override Krypton's automated trading zones."
                                                                            )}
                                                                        </Form.Label>
                                                                        <Button
                                                                            className="mx-3"
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={() => handleOpenTradingZoneModal()}>
                                                                            Add Trading Zone
                                                                        </Button>

                                                                        <div style={{
                                                                            minHeight: "200px",
                                                                            maxHeight: "60vh",
                                                                            overflowY: "auto",
                                                                            resize: "vertical",
                                                                            border: '1px solid #ccc',
                                                                            background: 'white',
                                                                            borderRadius: '5px',
                                                                            padding: '5px',
                                                                        }}>
                                                                            <TradingZonesTable
                                                                                tradingZones={currentSettingsGroup.tradingZones}
                                                                                handleEditZone={handleOpenTradingZoneEditModal}
                                                                                handleDeleteZone={handleDeleteTradingZone}
                                                                            />
                                                                        </div>
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                            )}
                                                            <Row>
                                                                <Col>
                                                                    <div>
                                                                        <Button
                                                                            variant="outline-dark"
                                                                            onClick={(e) => ClearForm(e)}
                                                                        >
                                                                            Reset Form
                                                                        </Button>
                                                                    </div>
                                                                </Col>

                                                                <Col className="d-flex justify-content-center">
                                                                    <Button
                                                                        type="submit"
                                                                        onClick={async (e) => await SubmitTradeConfig(e)}
                                                                    >
                                                                        Submit
                                                                    </Button>
                                                                </Col>
                                                                <Col>
                                                                </Col>
                                                            </Row>
                                                        </Form >
                                                    )}
                                            </Col >
                                            <Col></Col>
                                        </Row >
                                    </Container >
                                    <Container>
                                        <Row>
                                            <Col></Col>
                                            <Col xs={10} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left">
                                                <Row className="border-bottom mb-2 pb-2">
                                                    <Col>
                                                        <h3>
                                                            Trade Log
                                                        </h3>
                                                    </Col>
                                                    <Col className="d-flex justify-content-end">
                                                        <ButtonGroup>
                                                            <Button
                                                                className="me-2"
                                                                variant="outline-dark"
                                                                onClick={() => getTradeLog('personal')}>
                                                                Refresh
                                                            </Button>
                                                        </ButtonGroup>
                                                        {connections[activeBuid]?.broker !== BROKERAGE_SCHWAB && (
                                                        <ButtonGroup>
                                                            <Button
                                                                variant="danger"
                                                                onClick={() => liquidate()}>
                                                                Flatten All
                                                            </Button>
                                                        </ButtonGroup>
                                                        )}
                                                    </Col>
                                                </Row>
                                                <TradeTable />
                                                {/* <Tabs
                                                    defaultActiveKey="your-trade-log"
                                                    transition={false}
                                                    className="mb-3"
                                                    fill
                                                >
                                                    <Tab eventKey="your-trade-log" title="My Trade Log">
                                                        <TradeTable />
                                                    </Tab>
                                                    <Tab eventKey="full-trade-log" title="Full Trade Log">
                                                        <FullTradeTable />
                                                    </Tab>
                                                </Tabs> */}
                                            </Col>
                                            <Col></Col>
                                        </Row>
                                    </Container>
                                </div>
                            )
                        }
                    </>
                )
            }
        </>
    )
}

